/**
 * SegoRoomCharges (Fase 5.2)
 *
 * Panel embebido dentro de ReservationDashboard que muestra los cargos del
 * restaurante (vía Sego) aplicados al folio de la reserva. Consume:
 *   GET /api/room-charges-admin/by-reservation/:reservationId?includeReversed=true
 *
 * Diseño:
 *   - Banner con totales (cargos posted y reversados)
 *   - Accordion por cargo con items, fecha, postedBy, status
 *   - Empty state amigable cuando no hay cargos
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableHead, TableRow, Divider, Paper,
  CircularProgress, Alert
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { getApi } from 'views/services/api';
import moment from 'moment';

function fmtMoney(n, currency = 'USD') {
  if (n == null) return '—';
  return `${currency === 'USD' ? '$' : ''}${Number(n).toFixed(2)}${
    currency !== 'USD' ? ` ${currency}` : ''
  }`;
}

export default function SegoRoomCharges({ reservationId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancel = false;
    async function fetch() {
      if (!reservationId) return;
      setLoading(true);
      setError('');
      try {
        const r = await getApi(
          `api/room-charges-admin/by-reservation/${reservationId}?includeReversed=true`
        );
        if (cancel) return;
        if (r?.data?.charges) setData(r.data);
        else setError(r?.data?.error?.message || 'Sin respuesta del servidor');
      } catch (e) {
        if (!cancel) setError(e.message || 'Error de red');
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    fetch();
    return () => { cancel = true; };
  }, [reservationId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
    );
  }
  if (!data) return null;

  const charges = data.charges || [];
  const currency = data.currency || 'USD';
  const posted = data.totals?.postedTotal || 0;
  const reversed = data.totals?.reversedTotal || 0;
  const net = posted - reversed;
  const hasCharges = charges.length > 0;

  return (
    <Box sx={{ py: 1 }}>
      {/* Resumen */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          background: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)',
          border: '1px solid #fdba74',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <RoomServiceIcon sx={{ fontSize: 32, color: '#c2410c' }} />
          <Box flex={1}>
            <Typography variant="overline" sx={{ color: '#9a3412', fontWeight: 700 }}>
              Cargos del restaurante (Sego)
            </Typography>
            <Typography variant="h5" sx={{ color: '#7c2d12', fontWeight: 700 }}>
              {fmtMoney(net, currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9a3412' }}>
              {data.totals?.postedCount || 0} cargo(s) aplicado(s)
              {data.totals?.reversedCount > 0 && ` · ${data.totals.reversedCount} reversado(s) (${fmtMoney(reversed, currency)})`}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {!hasCharges ? (
        <Box sx={{ textAlign: 'center', py: 5, color: '#9ca3af' }}>
          <RestaurantIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
          <Typography>Esta reserva todavía no tiene cargos del restaurante.</Typography>
          <Typography variant="caption">
            Cuando Sego cargue un consumo al folio aparecerá aquí.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {charges.map((c) => (
            <Accordion
              key={c.chargeId}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: c.status === 'reversed' ? '#fecaca' : '#e5e7eb',
                borderRadius: 2,
                '&:before': { display: 'none' },
                bgcolor: c.status === 'reversed' ? '#fef2f2' : '#fff',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                  <ReceiptLongIcon
                    sx={{ color: c.status === 'reversed' ? '#b91c1c' : '#ea580c' }}
                  />
                  <Box flex={1} minWidth={0}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      {c.items?.length || 0} item(s) · {fmtMoney(c.total, c.currency || currency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Hab. {c.roomNo} · {moment(c.postedAt).format('DD/MM/YYYY HH:mm')}
                      {c.postedBy && ` · ${c.postedBy}`}
                    </Typography>
                  </Box>
                  {c.status === 'reversed' ? (
                    <Chip
                      size="small"
                      icon={<RemoveCircleOutlineIcon />}
                      label="Reversado"
                      sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600 }}
                    />
                  ) : (
                    <Chip size="small" label="Aplicado" color="success" sx={{ fontWeight: 600 }} />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Cant.</TableCell>
                      <TableCell align="right">Precio unit.</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(c.items || []).map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.itemName}</TableCell>
                        <TableCell align="right">{it.quantity}</TableCell>
                        <TableCell align="right">{fmtMoney(it.unitPrice, c.currency || currency)}</TableCell>
                        <TableCell align="right">{fmtMoney(it.lineTotal, c.currency || currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={0.5}>
                  <Row label="Subtotal" value={fmtMoney(c.subTotal, c.currency || currency)} />
                  {c.tax > 0 && <Row label="Impuestos" value={fmtMoney(c.tax, c.currency || currency)} />}
                  {c.serviceCharge > 0 && <Row label="Servicio" value={fmtMoney(c.serviceCharge, c.currency || currency)} />}
                  <Row
                    label="Total"
                    value={fmtMoney(c.total, c.currency || currency)}
                    bold
                  />
                </Stack>

                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Tag label="Orden Sego" value={c.externalOrderId} />
                    {c.externalKotId && <Tag label="KOT" value={c.externalKotId} />}
                    {c.externalInvoiceId && <Tag label="Factura Sego" value={c.externalInvoiceId} />}
                    <Tag label="Charge ID" value={c.chargeId} />
                  </Stack>
                  {c.status === 'reversed' && c.reverseReason && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      Motivo reversa: {c.reverseReason} · {moment(c.reversedAt).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function Row({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: bold ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500 }}>
        {value}
      </Typography>
    </Stack>
  );
}

function Tag({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Box>
  );
}
